import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Target, DollarSign, Users, Clock, Award, BarChart3, Activity, Zap, CheckCircle, AlertTriangle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area } from 'recharts';

const PerformanceMetrics = ({ metrics, campaigns = [] }) => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [comparisonPeriod, setComparisonPeriod] = useState('previous');

  // Mock performance data - in real implementation, fetch from Supabase
  const performanceData = {
    overview: {
      totalRevenue: 287500,
      revenueGrowth: 18.5,
      totalDeals: 156,
      dealsGrowth: 12.8,
      avgDealSize: 1843,
      dealSizeGrowth: 5.2,
      closeRate: 15.8,
      closeRateChange: -2.1,
      salesCycle: 18.4,
      salesCycleChange: -3.2
    },
    monthly: [
      { month: 'Jul 24', revenue: 185000, deals: 98, avgDeal: 1888, closeRate: 14.2 },
      { month: 'Aug 24', revenue: 192000, deals: 102, avgDeal: 1882, closeRate: 15.1 },
      { month: 'Sep 24', revenue: 208000, deals: 115, avgDeal: 1809, closeRate: 16.3 },
      { month: 'Oct 24', revenue: 235000, deals: 128, avgDeal: 1836, closeRate: 17.2 },
      { month: 'Nov 24', revenue: 251000, deals: 142, avgDeal: 1768, closeRate: 18.1 },
      { month: 'Dec 24', revenue: 287500, deals: 156, avgDeal: 1843, closeRate: 15.8 }
    ],
    channelPerformance: [
      { channel: 'Phone', deals: 68, revenue: 142500, rate: 22.4, avgDeal: 2096 },
      { channel: 'Email', deals: 52, revenue: 89500, rate: 12.8, avgDeal: 1721 },
      { channel: 'SMS', deals: 23, revenue: 35000, rate: 8.9, avgDeal: 1522 },
      { channel: 'Social', deals: 13, revenue: 20500, rate: 15.7, avgDeal: 1577 }
    ],
    leadSources: [
      { source: 'Website', value: 35, color: '#3B82F6', revenue: 89250 },
      { source: 'Referral', value: 28, color: '#10B981', revenue: 71500 },
      { source: 'LinkedIn', value: 18, color: '#8B5CF6', revenue: 52750 },
      { source: 'Email Campaign', value: 12, color: '#F59E0B', revenue: 35000 },
      { source: 'Other', value: 7, color: '#6B7280', revenue: 39000 }
    ],
    aiImpact: {
      totalInteractions: 2847,
      aiGeneratedScripts: 234,
      aiRecommendationsFollowed: 189,
      improvementFromAI: 23.7,
      timesSaved: 127,
      additionalRevenue: 42750
    },
    topPerformers: [
      { name: 'Sarah Johnson', deals: 23, revenue: 48500, rate: 28.4 },
      { name: 'Michael Chen', deals: 19, revenue: 41200, rate: 24.1 },
      { name: 'Emily Rodriguez', deals: 17, revenue: 38750, rate: 22.8 }
    ]
  };

  const getChangeColor = (value) => {
    return value >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getChangeIcon = (value) => {
    return value >= 0 ? TrendingUp : TrendingDown;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    })?.format(value);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value?.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="flex gap-2">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="12m">Last 12 Months</option>
          </select>
          
          <select
            value={comparisonPeriod}
            onChange={(e) => setComparisonPeriod(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="previous">vs Previous Period</option>
            <option value="year">vs Same Period Last Year</option>
            <option value="target">vs Target</option>
          </select>
        </div>
      </div>
      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div className={`flex items-center ${getChangeColor(performanceData?.overview?.revenueGrowth)}`}>
              {React.createElement(getChangeIcon(performanceData?.overview?.revenueGrowth), { className: 'h-4 w-4 mr-1' })}
              <span className="text-sm font-medium">
                {formatPercentage(performanceData?.overview?.revenueGrowth)}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Total Revenue</p>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(performanceData?.overview?.totalRevenue)}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <Target className="h-8 w-8 text-blue-600" />
            <div className={`flex items-center ${getChangeColor(performanceData?.overview?.dealsGrowth)}`}>
              {React.createElement(getChangeIcon(performanceData?.overview?.dealsGrowth), { className: 'h-4 w-4 mr-1' })}
              <span className="text-sm font-medium">
                {formatPercentage(performanceData?.overview?.dealsGrowth)}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Total Deals</p>
            <p className="text-3xl font-bold text-gray-900">{performanceData?.overview?.totalDeals}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <BarChart3 className="h-8 w-8 text-purple-600" />
            <div className={`flex items-center ${getChangeColor(performanceData?.overview?.dealSizeGrowth)}`}>
              {React.createElement(getChangeIcon(performanceData?.overview?.dealSizeGrowth), { className: 'h-4 w-4 mr-1' })}
              <span className="text-sm font-medium">
                {formatPercentage(performanceData?.overview?.dealSizeGrowth)}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Avg Deal Size</p>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(performanceData?.overview?.avgDealSize)}
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <Activity className="h-8 w-8 text-orange-600" />
            <div className={`flex items-center ${getChangeColor(performanceData?.overview?.closeRateChange)}`}>
              {React.createElement(getChangeIcon(performanceData?.overview?.closeRateChange), { className: 'h-4 w-4 mr-1' })}
              <span className="text-sm font-medium">
                {formatPercentage(performanceData?.overview?.closeRateChange)}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Close Rate</p>
            <p className="text-3xl font-bold text-gray-900">{performanceData?.overview?.closeRate}%</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <Clock className="h-8 w-8 text-indigo-600" />
            <div className={`flex items-center ${getChangeColor(performanceData?.overview?.salesCycleChange)}`}>
              {React.createElement(getChangeIcon(performanceData?.overview?.salesCycleChange), { className: 'h-4 w-4 mr-1' })}
              <span className="text-sm font-medium">
                {formatPercentage(performanceData?.overview?.salesCycleChange)}
              </span>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Sales Cycle</p>
            <p className="text-3xl font-bold text-gray-900">{performanceData?.overview?.salesCycle} days</p>
          </div>
        </motion.div>
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Revenue Trend</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedMetric('revenue')}
                className={`px-3 py-1 rounded text-sm ${
                  selectedMetric === 'revenue' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Revenue
              </button>
              <button
                onClick={() => setSelectedMetric('deals')}
                className={`px-3 py-1 rounded text-sm ${
                  selectedMetric === 'deals' ?'bg-blue-600 text-white' :'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Deals
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={performanceData?.monthly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => selectedMetric === 'revenue' ? formatCurrency(value) : value} />
              <Area 
                type="monotone" 
                dataKey={selectedMetric} 
                stroke="#3B82F6" 
                fill="#3B82F6" 
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Lead Sources */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Sources Performance</h3>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <ResponsiveContainer width="100%" height={200}>
                <RechartsPieChart>
                  <Pie
                    data={performanceData?.leadSources}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={false}
                  >
                    {performanceData?.leadSources?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            <div className="ml-6 space-y-3">
              {performanceData?.leadSources?.map((source, index) => (
                <div key={index} className="flex items-center">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: source?.color }}
                  ></div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{source?.source}</div>
                    <div className="text-xs text-gray-500">
                      {formatCurrency(source?.revenue)} • {source?.value}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Channel Performance */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Channel Performance Analysis</h3>
          <p className="text-gray-600 mt-1">Compare performance across different sales channels</p>
        </div>
        
        <div className="p-6">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData?.channelPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="channel" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="deals" fill="#3B82F6" name="Deals Closed" />
              <Bar yAxisId="left" dataKey="revenue" fill="#10B981" name="Revenue ($)" />
              <Bar yAxisId="right" dataKey="rate" fill="#F59E0B" name="Close Rate (%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* AI Impact & Top Performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AI Impact */}
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Zap className="h-5 w-5 mr-2 text-purple-600" />
              AI Impact Analysis
            </h3>
            <Award className="h-8 w-8 text-purple-600" />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-900">234</div>
              <div className="text-sm text-gray-600">AI Scripts Generated</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-green-900">189</div>
              <div className="text-sm text-gray-600">Recommendations Followed</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-900">127h</div>
              <div className="text-sm text-gray-600">Time Saved</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl font-bold text-orange-900">{formatCurrency(42750)}</div>
              <div className="text-sm text-gray-600">Additional Revenue</div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Performance Improvement with AI</span>
              <div className="flex items-center text-green-600">
                <TrendingUp className="h-4 w-4 mr-1" />
                <span className="text-lg font-bold">+{performanceData?.aiImpact?.improvementFromAI}%</span>
              </div>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full" 
                style={{ width: `${Math.min(performanceData?.aiImpact?.improvementFromAI * 2, 100)}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Users className="h-5 w-5 mr-2 text-blue-600" />
              Top Performers
            </h3>
            <Award className="h-6 w-6 text-yellow-500" />
          </div>
          
          <div className="space-y-4">
            {performanceData?.topPerformers?.map((performer, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                    index === 0 ? 'bg-yellow-500' :
                    index === 1 ? 'bg-gray-400': 'bg-orange-600'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="ml-3">
                    <div className="font-medium text-gray-900">{performer?.name}</div>
                    <div className="text-sm text-gray-500">{performer?.deals} deals closed</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-gray-900">{formatCurrency(performer?.revenue)}</div>
                  <div className="text-sm text-green-600">{performer?.rate}% close rate</div>
                </div>
              </div>
            ))}
          </div>
          
          <button className="w-full mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            View Full Leaderboard
          </button>
        </div>
      </div>
      {/* Performance Insights */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Performance Insights & Recommendations</h3>
          <p className="text-gray-600 mt-1">AI-powered analysis of your sales performance</p>
        </div>
        
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
              What's Working Well
            </h4>
            <ul className="space-y-2">
              <li className="flex items-start text-sm text-gray-700">
                <ArrowUpRight className="h-4 w-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                Phone calls show 22.4% close rate, 40% higher than average
              </li>
              <li className="flex items-start text-sm text-gray-700">
                <ArrowUpRight className="h-4 w-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                AI-generated scripts increased conversion by 23.7%
              </li>
              <li className="flex items-start text-sm text-gray-700">
                <ArrowUpRight className="h-4 w-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                Website leads have highest revenue potential per deal
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-3 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-2 text-orange-500" />
              Areas for Improvement
            </h4>
            <ul className="space-y-2">
              <li className="flex items-start text-sm text-gray-700">
                <ArrowDownRight className="h-4 w-4 mr-2 text-red-500 mt-0.5 flex-shrink-0" />
                SMS channel underperforming at 8.9% close rate
              </li>
              <li className="flex items-start text-sm text-gray-700">
                <ArrowDownRight className="h-4 w-4 mr-2 text-red-500 mt-0.5 flex-shrink-0" />
                Close rate declined 2.1% compared to previous period
              </li>
              <li className="flex items-start text-sm text-gray-700">
                <ArrowDownRight className="h-4 w-4 mr-2 text-red-500 mt-0.5 flex-shrink-0" />
                Email campaigns need better personalization
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMetrics;